import { ShellLeftInspector } from '../../shared/ui/inspector';
import {
  CSSProperties,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { SidebarInset, SidebarProvider } from '../../shared/ui/sidebar';
import { MobileBottomNav, type MobileProjectNavContext } from './MobileBottomNav';
import { MobileInspectorScreen } from './MobileInspectorScreen';
import {
  MOBILE_BOTTOM_NAV_HEIGHT_VAR,
  composeMobileBottomNavGeometry,
} from './mobileBottomNavGeometry';
import {
  APP_SHELL_DIMENSIONS,
  getAppShellContentPaddingClass,
  shouldPreferNativeHeader,
  shouldPreferNativeNavigation,
  shouldUseFramelessAppShell,
  isStandalonePwaDisplayMode,
  useAppShellFrame,
  getShellPanelMotionTiming,
} from '../../layout-system';
import type { AppLayoutScrollMode } from '../../layout-system';
import type { AppShellPreset } from '../../layout-system';
import { cn } from '../../shared/ui/utils';
import { ScrollEdgeBlur } from '../../shared/ui/scroll-edge-blur';
import { ShellPullToRefresh } from '../../shared/ui/pull-to-refresh';
import { useNativeBridge } from '../../hooks/useNativeBridge';
import { DataService } from '../../services/DataService';
import { NATIVE_REFRESH_EVENT } from '../../types/native';
import { ErrorBoundary } from '../ErrorBoundary';
import { isProjectAdminTab } from '../../features/projects/admin/navigation';
import { getDraggedBottomPanelHeight } from './bottomPanelResize';

export type { AppLayoutScrollMode } from '../../layout-system';

interface AppLayoutProps {
  user: any;
  children: ReactNode;
  sidebar?: ReactNode;
  header?: ReactNode;
  collapseSidebarForView?: boolean;
  isLeftPanelOpen?: boolean;
  onCloseLeftPanel?: () => void;
  rightPanel?: ReactNode;
  isRightPanelOpen?: boolean;
  /** Render the inspector as a separate inset workspace beside the page. */
  rightPanelDetached?: boolean;
  /** The panel shares the body with the page as an equal split: flush, folding the sidebar like a detached
   *  inspector, and never wider than leaves `rightPanelSiblingMinWidth` for the page as the window narrows. */
  rightPanelSplit?: boolean;
  /** Optional product-specific width for the desktop right-panel slot. */
  rightPanelWidth?: string;
  /** Let the user resize the desktop right-panel slot from its left edge. */
  rightPanelResizable?: boolean;
  /** Smallest allowed panel width while resizing. Default 400px. */
  rightPanelMinWidth?: number;
  /** Largest allowed panel width while resizing. */
  rightPanelMaxWidth?: number;
  /** Minimum width retained for the content beside the panel. Default 360px. */
  rightPanelSiblingMinWidth?: number;
  /** Persists the user's chosen width for this product surface. */
  rightPanelWidthStorageKey?: string;
  /** Overrides the shared shell duration for product-specific panel motion. */
  panelMotionDurationMs?: number;
  /** Uses the same slide timing as the navigation rail for paired surfaces. */
  rightPanelMotionKind?: 'panel' | 'slide';
  onCloseRightPanel?: () => void;
  bottomPanel?: ReactNode;
  isBottomPanelOpen?: boolean;
  /** Separate the bottom surface from the page and keep it below the main column. */
  bottomPanelDetached?: boolean;
  /** Let the user resize the desktop bottom-panel slot from its top edge. */
  bottomPanelResizable?: boolean;
  /** Smallest allowed bottom-panel height while resizing. Default 144px. */
  bottomPanelMinHeight?: number;
  /** Largest allowed bottom-panel height while resizing. */
  bottomPanelMaxHeight?: number;
  /** Minimum height retained for the content above the panel. Default 240px. */
  bottomPanelSiblingMinHeight?: number;
  /** Persists the user's chosen bottom-panel height for this product surface. */
  bottomPanelHeightStorageKey?: string;
  /** Fit the initial panel height to its rendered table rows instead of leaving blank space. */
  bottomPanelAutoFitRows?: boolean;
  mobileRightPanelMode?: 'drawer' | 'inline';
  noPadding?: boolean;
  scrollMode?: AppLayoutScrollMode;
  layoutPreset?: AppShellPreset;
  currentView?: string;
  onNavigate?: (view: string) => void;
  onViewChange?: (view: string) => void;
  onLogout?: () => void;
  projectContext?: MobileProjectNavContext;
  /** Extra props passed from AppContent — accepted but not directly used by layout */
  onShowNotifications?: () => void;
  onShowMessages?: (chatId?: string) => void;
  notificationOpen?: boolean;
  messagePanelOpen?: boolean;
  panelChatId?: string | null;
  onClosePanels?: () => void;
  hideBottomNav?: boolean;
  hideHeader?: boolean;
}

/**
 * AppLayout component that follows iOS/macOS design system guidelines.
 * Features a "frameless" floating panel look where the base canvas is visible.
 */
export function AppLayout({
  user,
  children,
  sidebar,
  header,
  collapseSidebarForView = false,
  isLeftPanelOpen = false,
  onCloseLeftPanel,
  rightPanel,
  isRightPanelOpen = false,
  rightPanelDetached = false,
  rightPanelSplit = false,
  rightPanelWidth,
  rightPanelResizable = false,
  rightPanelMinWidth = 400,
  rightPanelMaxWidth,
  rightPanelSiblingMinWidth = 360,
  rightPanelWidthStorageKey,
  panelMotionDurationMs,
  rightPanelMotionKind = 'panel',
  onCloseRightPanel,
  bottomPanel,
  isBottomPanelOpen = false,
  bottomPanelDetached = false,
  bottomPanelResizable = false,
  bottomPanelMinHeight = 144,
  bottomPanelMaxHeight,
  bottomPanelSiblingMinHeight = 240,
  bottomPanelHeightStorageKey,
  bottomPanelAutoFitRows = false,
  mobileRightPanelMode = 'drawer',
  noPadding = false,
  scrollMode = 'platform',
  layoutPreset = 'content',
  currentView = 'dashboard',
  onNavigate,
  onViewChange,
  onLogout,
  projectContext,
  // Accept but don't destructure unused panel props
  onShowNotifications: _onShowNotifications,
  onShowMessages: _onShowMessages,
  notificationOpen: _notificationOpen,
  messagePanelOpen: _messagePanelOpen,
  panelChatId: _panelChatId,
  onClosePanels: _onClosePanels,
  hideBottomNav = false,
  hideHeader = false,
}: AppLayoutProps) {
  const contentOutletRef = useRef<HTMLElement | null>(null);
  const [leftPanelWidth, setLeftPanelWidth] = useState(240);
  const rightPanelRef = useRef<HTMLDivElement | null>(null);
  const rightPanelAnimationRef = useRef<Animation | null>(null);
  const rightPanelResizeCleanupRef = useRef<(() => void) | null>(null);
  const rightPanelSnapshotRef = useRef<ReactNode>(null);
  const bottomPanelRef = useRef<HTMLDivElement | null>(null);
  const bottomPanelAnimationRef = useRef<Animation | null>(null);
  const bottomPanelResizeCleanupRef = useRef<(() => void) | null>(null);
  const bottomPanelManuallyResizedRef = useRef(false);
  const bottomPanelSnapshotRef = useRef<ReactNode>(null);
  const mouseDragScrollRef = useRef<{
    pointerId: number;
    startX: number;
    startY: number;
    startScrollTop: number;
    axis: 'pending' | 'vertical' | 'horizontal';
    outlet: HTMLElement;
  } | null>(null);
  const suppressMouseDragClickRef = useRef(false);
  const suppressMouseDragClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [stableRightPanel, setStableRightPanel] = useState<ReactNode>(null);
  const [stableRightPanelDetached, setStableRightPanelDetached] = useState(false);
  const [resizedRightPanelWidth, setResizedRightPanelWidth] = useState<number | null>(null);
  const [stableRightPanelWidth, setStableRightPanelWidth] = useState<string | undefined>(rightPanelWidth);
  const [stableBottomPanel, setStableBottomPanel] = useState<ReactNode>(null);
  const [stableBottomPanelDetached, setStableBottomPanelDetached] = useState(false);
  const [resizedBottomPanelHeight, setResizedBottomPanelHeight] = useState<number | null>(null);
  const configuredRightPanelWidth = rightPanelResizable && resizedRightPanelWidth !== null
    ? `${resizedRightPanelWidth}px`
    : rightPanelWidth;
  const chosenRightPanelWidth = configuredRightPanelWidth ?? stableRightPanelWidth;
  const effectiveRightPanelWidth = rightPanelSplit && chosenRightPanelWidth
    ? `min(${chosenRightPanelWidth}, max(${rightPanelMinWidth}px, calc(100vw - var(--qs-sidebar-actual-width, 0px) - ${rightPanelSiblingMinWidth}px)))`
    : chosenRightPanelWidth;

  // Refs capture the last live panel during render, before a close-state
  // commit can remove the parent's panel props. State then performs the
  // eventual unmount after the JavaScript animation finishes.
  if (isRightPanelOpen && rightPanel !== null && rightPanel !== undefined) {
    rightPanelSnapshotRef.current = rightPanel;
  }
  if (isBottomPanelOpen && bottomPanel !== null && bottomPanel !== undefined) {
    bottomPanelSnapshotRef.current = bottomPanel;
  }

  // Capture the active inspector before paint. The live prop renders on open,
  // while this stable copy stays mounted until the JavaScript-driven close
  // animation has actually finished.
  useLayoutEffect(() => {
    if (!isRightPanelOpen) return;
    setStableRightPanelDetached(rightPanelDetached);
    if (rightPanel !== null && rightPanel !== undefined) {
      setStableRightPanel(rightPanel);
    }
    if (configuredRightPanelWidth !== undefined) {
      setStableRightPanelWidth(configuredRightPanelWidth);
    }
  }, [configuredRightPanelWidth, isRightPanelOpen, rightPanel, rightPanelDetached]);

  useLayoutEffect(() => {
    if (!isBottomPanelOpen) return;
    setStableBottomPanelDetached(bottomPanelDetached);
    if (bottomPanel !== null && bottomPanel !== undefined) {
      setStableBottomPanel(bottomPanel);
    }
  }, [bottomPanel, bottomPanelDetached, isBottomPanelOpen]);

  useEffect(() => () => {
    rightPanelResizeCleanupRef.current?.();
    bottomPanelResizeCleanupRef.current?.();
    rightPanelAnimationRef.current?.cancel();
    bottomPanelAnimationRef.current?.cancel();
  }, []);

  const nativeBridge = useNativeBridge();

  // Track browser-chrome bottom overlap via the Visual Viewport API.
  // In mobile Safari/Chrome the bottom address-bar/toolbar overlays the
  // layout viewport (`position:fixed; bottom:0` sits behind it). The visual
  // viewport reports the actually-visible area, so the difference equals
  // the chrome height. Native shells get 0 — they own their own chrome.
  useEffect(() => {
    if (typeof window === 'undefined' || nativeBridge.isNative) {
      document.documentElement.style.setProperty('--qs-browser-chrome-bottom', '0px');
      return;
    }
    // Installed PWAs (standalone / fullscreen display modes) have no browser
    // address bar at the bottom, so there is nothing to clear — force 0 and
    // skip the visual-viewport measurement entirely. This also keeps the
    // on-screen keyboard from ever feeding into this var in a PWA, since the
    // measurement (which conflates the two) never runs.
    const isStandalonePwa = isStandalonePwaDisplayMode();
    if (isStandalonePwa) {
      document.documentElement.style.setProperty('--qs-browser-chrome-bottom', '0px');
      return;
    }
    const vv = window.visualViewport;
    if (!vv) {
      document.documentElement.style.setProperty('--qs-browser-chrome-bottom', '0px');
      return;
    }
    let rafId: number | null = null;
    let lastOverlap = -1;
    const apply = () => {
      rafId = null;
      // innerHeight - visualViewport.height also captures the on-screen
      // keyboard (~250-350px), not just the browser's bottom address bar
      // (~44-88px). Without a ceiling, an open keyboard (e.g. in Messages)
      // sets this var to the keyboard height; when the user returns to a
      // view with the fixed bottom nav before the viewport settles, the nav
      // renders lifted by that stale value and floats halfway up the screen.
      // Browser chrome never exceeds ~120px, so clamp there — the keyboard
      // can no longer leak into the nav's position.
      const MAX_BROWSER_CHROME = 120;
      const overlap = Math.min(
        MAX_BROWSER_CHROME,
        Math.max(0, Math.round(window.innerHeight - vv.height - vv.offsetTop))
      );
      // Skip CSS writes when the value hasn't changed — vv.scroll fires
      // continuously while the user is dragging and most events report
      // the same overlap.
      if (overlap === lastOverlap) return;
      lastOverlap = overlap;
      document.documentElement.style.setProperty('--qs-browser-chrome-bottom', `${overlap}px`);
    };
    const schedule = () => {
      if (rafId !== null) return;
      rafId = window.requestAnimationFrame(apply);
    };
    // bfcache restore can leave a stale value — force a fresh read on
    // pageshow. orientationchange fires before vv settles, so re-read
    // both immediately and on the next frame.
    const onPageShow = () => schedule();
    const onOrientationChange = () => {
      schedule();
      window.setTimeout(schedule, 250);
    };
    apply();
    vv.addEventListener('resize', schedule);
    vv.addEventListener('scroll', schedule);
    window.addEventListener('pageshow', onPageShow);
    window.addEventListener('orientationchange', onOrientationChange);
    return () => {
      if (rafId !== null) window.cancelAnimationFrame(rafId);
      vv.removeEventListener('resize', schedule);
      vv.removeEventListener('scroll', schedule);
      window.removeEventListener('pageshow', onPageShow);
      window.removeEventListener('orientationchange', onOrientationChange);
    };
  }, [nativeBridge.isNative]);

  // Publish the sidebar's actual rendered width as `--qs-sidebar-actual-width`
  // so any consumer that needs to offset content past the sidebar can pull
  // a live measurement instead of the static `--sidebar-width` source var.
  // The static var is what *drives* the sidebar's flex-basis; the
  // ResizeObserver below tracks what the layout actually renders, which
  // includes mid-transition values when the sidebar collapses/expands. If
  // the sidebar is offcanvas (mobile / collapsed) the var goes to 0,
  // letting the canvas reach edge-to-edge dynamically.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const root = document.documentElement;
    let observer: ResizeObserver | null = null;
    let lateMount: MutationObserver | null = null;
    let rafId: number | null = null;
    let observed: HTMLElement | null = null;

    /** Re-wire whenever the element we are measuring leaves the document, or a
     *  sidebar appears when we had none. Idempotent — safe to call repeatedly. */
    const watchForSidebar = () => {
      lateMount?.disconnect();
      lateMount = new MutationObserver(() => {
        const live = document.querySelector('[data-slot="context-navigation-rails"], [data-slot="sidebar-container"], [data-slot="sidebar"]');
        if (observed && observed.isConnected && live === observed) return;
        if (!live) return;
        observer?.disconnect();
        observer = null;
        framesRemaining = 120;
        wireUp();
      });
      lateMount.observe(document.body, { childList: true, subtree: true });
    };
    let framesRemaining = 120;
    const setVar = (width: number) => {
      root.style.setProperty('--qs-sidebar-actual-width', `${Math.round(width)}px`);
    };
    const wireUp = () => {
      // Both slots, in this order: shadcn's Sidebar renders TWO different
      // trees. `collapsible="none"` (what AppSidebar uses) EARLY-RETURNS a
      // plain `[data-slot="sidebar"]` div and never creates
      // `sidebar-container`, so querying only the container never resolved on
      // web — this var went unpublished and every consumer that insets past
      // the sidebar fell back to 0.
      const el = (document.querySelector('[data-slot="context-navigation-rails"]')
        ?? document.querySelector('[data-slot="sidebar-container"]')
        ?? document.querySelector('[data-slot="sidebar"]')) as HTMLElement | null;
      if (!el) {
        // Sidebar may not be in the DOM on the first frame (lazy or
        // suppressed by the native shell). Retry next frame — but bounded:
        // it may never mount at all. The native shells suppress it outright,
        // and on mobile it is an offcanvas drawer that exists only while
        // open, so an unbounded retry burns a rAF every frame for the life
        // of the session. Give up after a short grace period and publish 0,
        // which is what "no sidebar" means to every consumer anyway.
        if (framesRemaining-- <= 0) {
          setVar(0);
          watchForSidebar();
          return;
        }
        rafId = requestAnimationFrame(wireUp);
        return;
      }
      observed = el;
      setVar(el.getBoundingClientRect().width);
      // Stay watching for the whole effect's life. The sidebar unmounts and
      // remounts across route changes, and a ResizeObserver left on a detached
      // node reports 0 forever — which is exactly how this var went back to 0
      // after the first recovery. One-shot recovery is not enough.
      watchForSidebar();
      observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          // contentRect excludes padding; the floating sidebar container has
          // padding, so it under-reports vs the initial getBoundingClientRect()
          // read above. Use the border box so both readings agree.
          const borderWidth = entry.borderBoxSize?.[0]?.inlineSize
            ?? entry.target.getBoundingClientRect().width;
          setVar(borderWidth);
        }
      });
      observer.observe(el);
    };
    wireUp();
    return () => {
      if (rafId !== null) cancelAnimationFrame(rafId);
      if (observer) observer.disconnect();
      if (lateMount) lateMount.disconnect();
      root.style.removeProperty('--qs-sidebar-actual-width');
    };
  }, []);

  const {
    isMobile,
    shellRuntime,
    shellPlatform,
    resolvedScrollMode,
    isShellLocked,
    showBottomNav,
    contentPaddingClass,
  } = useAppShellFrame({
    currentView,
    scrollMode,
    noPadding,
  });

  // Detached inspectors use two canvas surfaces. Reclaim the expanded rail's
  // width while either canvas is open, then restore it only when this shell
  // performed the collapse and the user has not expanded it meanwhile.
  const inspectorCollapsedSidebarRef = useRef(false);
  const inspectorWasOpenRef = useRef(false);
  const sidebarCollapsedRef = useRef(false);
  const pendingSidebarCollapseRef = useRef(false);
  useLayoutEffect(() => {
    const onSidebarState = (event: Event) => {
      const collapsed = !!(event as CustomEvent<{ collapsed?: boolean }>).detail?.collapsed;
      sidebarCollapsedRef.current = collapsed;
      if (collapsed) pendingSidebarCollapseRef.current = false;
      if (!collapsed && inspectorWasOpenRef.current && !pendingSidebarCollapseRef.current) {
        inspectorCollapsedSidebarRef.current = false;
      }
    };
    window.addEventListener('app-sidebar:state-change', onSidebarState);
    const sidebarElement = document.querySelector<HTMLElement>('[data-context-rail="true"]') ?? document.querySelector<HTMLElement>('[data-slot="sidebar"][data-collapsed]');
    sidebarCollapsedRef.current = sidebarElement?.dataset.collapsed === 'true';
    return () => window.removeEventListener('app-sidebar:state-change', onSidebarState);
  }, []);
  useLayoutEffect(() => {
    const inspectorOpen = !isMobile && (collapseSidebarForView || isLeftPanelOpen || (isRightPanelOpen && (rightPanelDetached || rightPanelSplit)));
    window.dispatchEvent(new CustomEvent('app-sidebar:inspector-state', { detail: { open: inspectorOpen } }));
    if (inspectorOpen && !inspectorWasOpenRef.current) {
      inspectorCollapsedSidebarRef.current = !sidebarCollapsedRef.current;
      if (!sidebarCollapsedRef.current) {
        pendingSidebarCollapseRef.current = true;
        window.dispatchEvent(new CustomEvent('app-sidebar:set-collapsed', { detail: { collapsed: true, instant: collapseSidebarForView || isLeftPanelOpen } }));
      }
    } else if (!inspectorOpen && inspectorWasOpenRef.current) {
      pendingSidebarCollapseRef.current = false;
      if (inspectorCollapsedSidebarRef.current && sidebarCollapsedRef.current) {
        window.dispatchEvent(new CustomEvent('app-sidebar:set-collapsed', { detail: { collapsed: false } }));
      }
      inspectorCollapsedSidebarRef.current = false;
    }
    inspectorWasOpenRef.current = inspectorOpen;
  }, [isMobile, isRightPanelOpen, rightPanelDetached, rightPanelSplit, isLeftPanelOpen, collapseSidebarForView]);

  // Escape closes whichever inspector is open in the shell's right panel, so
  // every view behaves the same without wiring its own key handler. A panel
  // or view that uses Escape for itself (cancel a drawing tool, close a menu
  // or dialog, clear a selection) calls preventDefault and keeps priority:
  // the close is deferred until every listener has run, then skipped if the
  // event was consumed.
  const closeRightPanelRef = useRef(onCloseRightPanel);
  useLayoutEffect(() => {
    closeRightPanelRef.current = onCloseRightPanel;
  }, [onCloseRightPanel]);
  useEffect(() => {
    if (!isRightPanelOpen && !isLeftPanelOpen) return;
    const panelSelector = '[data-slot="app-right-panel"], [data-slot="app-mobile-right-panel"], [data-slot="app-left-panel"]';
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.isComposing || event.defaultPrevented) return;
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) return;
      const target = event.target instanceof Element ? event.target : null;
      const insidePanel = Boolean(target?.closest(panelSelector));
      // Escape in a field elsewhere on the page (search, inline cell edit)
      // belongs to that field, not to the inspector.
      const inEditableField = Boolean(
        target?.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]'),
      );
      if (inEditableField && !insidePanel) return;
      // A modal, menu or popover layered above the panel closes first.
      const overlays = document.querySelectorAll(
        '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [aria-modal="true"], [data-radix-popper-content-wrapper]',
      );
      for (const overlay of overlays) {
        if (!overlay.closest(panelSelector)) return;
      }
      window.setTimeout(() => {
        if (event.defaultPrevented) return;
        if (isRightPanelOpen) closeRightPanelRef.current?.();
        else onCloseLeftPanel?.();
      }, 0);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isRightPanelOpen, isLeftPanelOpen, onCloseLeftPanel]);
  useEffect(() => () => {
    if (inspectorCollapsedSidebarRef.current && sidebarCollapsedRef.current) {
      window.dispatchEvent(new CustomEvent('app-sidebar:set-collapsed', { detail: { collapsed: false } }));
    }
  }, []);

  useLayoutEffect(() => {
    if (!rightPanelResizable) {
      setResizedRightPanelWidth(null);
      return;
    }

    if (!rightPanelWidthStorageKey || typeof window === 'undefined') return;

    try {
      const storedWidth = Number.parseFloat(window.localStorage.getItem(rightPanelWidthStorageKey) ?? '');
      setResizedRightPanelWidth(
        Number.isFinite(storedWidth)
          ? Math.min(
              rightPanelMaxWidth ?? Number.POSITIVE_INFINITY,
              Math.max(rightPanelMinWidth, Math.round(storedWidth)),
            )
          : null,
      );
    } catch {
      // Storage can be unavailable in privacy-restricted browser contexts;
      // resizing still works for the current session.
      setResizedRightPanelWidth(null);
    }
  }, [rightPanelMaxWidth, rightPanelMinWidth, rightPanelResizable, rightPanelWidthStorageKey]);

  useEffect(() => {
    if (isMobile) return;
    const right = rightPanelRef.current;
    const row = right?.parentElement;
    if (!row) return;
    const updateLeftCeiling = () => {
      const available = row.getBoundingClientRect().width - (right?.getBoundingClientRect().width || 0) - 360;
      row.style.setProperty('--qs-shell-left-max-width', `${Math.max(240, Math.min(360, available))}px`);
    };
    const observer = new ResizeObserver(updateLeftCeiling);
    observer.observe(row);
    if (right) observer.observe(right);
    updateLeftCeiling();
    return () => { observer.disconnect(); row.style.removeProperty('--qs-shell-left-max-width'); };
  }, [isMobile]);

  const getRightPanelResizeBounds = useCallback(() => {
    const panel = rightPanelRef.current;
    const hostWidth = panel?.parentElement?.getBoundingClientRect().width ?? 0;
    return {
      min: rightPanelMinWidth,
      max: Math.max(
        rightPanelMinWidth,
        Math.min(
          rightPanelMaxWidth ?? Number.POSITIVE_INFINITY,
          hostWidth - rightPanelSiblingMinWidth - (isLeftPanelOpen ? panel?.parentElement?.querySelector('.qs-shell-left-panel')?.getBoundingClientRect().width ?? leftPanelWidth : 0),
        ),
      ),
    };
  }, [rightPanelMaxWidth, rightPanelMinWidth, rightPanelSiblingMinWidth, isLeftPanelOpen, leftPanelWidth]);

  const applyRightPanelWidth = useCallback((width: number) => {
    const panel = rightPanelRef.current;
    if (!panel) return;
    const roundedWidth = Math.round(width);
    panel.style.setProperty('--qs-shell-right-panel-width', `${roundedWidth}px`);
    panel.style.width = `${roundedWidth}px`;
  }, []);

  const commitRightPanelWidth = useCallback((width: number) => {
    const { min, max } = getRightPanelResizeBounds();
    const nextWidth = Math.round(Math.min(max, Math.max(min, width)));
    applyRightPanelWidth(nextWidth);
    setResizedRightPanelWidth(nextWidth);

    if (rightPanelWidthStorageKey && typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(rightPanelWidthStorageKey, String(nextWidth));
      } catch {
        // The live width remains valid even when browser storage is blocked.
      }
    }
  }, [applyRightPanelWidth, getRightPanelResizeBounds, rightPanelWidthStorageKey]);

  const handleRightPanelResizeStart = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!rightPanelResizable || event.button !== 0) return;
    const panel = rightPanelRef.current;
    if (!panel) return;

    rightPanelResizeCleanupRef.current?.();
    rightPanelAnimationRef.current?.cancel();
    rightPanelAnimationRef.current = null;

    const startX = event.clientX;
    const startWidth = panel.getBoundingClientRect().width;
    const { min, max } = getRightPanelResizeBounds();
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    let nextWidth = Math.min(max, Math.max(min, startWidth));

    const cleanup = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleEnd);
      window.removeEventListener('pointercancel', handleEnd);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      panel.removeAttribute('data-resizing');
      rightPanelResizeCleanupRef.current = null;
    };
    const handleMove = (pointerEvent: PointerEvent) => {
      nextWidth = Math.min(max, Math.max(min, startWidth - (pointerEvent.clientX - startX)));
      applyRightPanelWidth(nextWidth);
    };
    const handleEnd = () => {
      cleanup();
      commitRightPanelWidth(nextWidth);
    };

    panel.setAttribute('data-resizing', 'true');
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleEnd);
    window.addEventListener('pointercancel', handleEnd);
    rightPanelResizeCleanupRef.current = cleanup;
    event.preventDefault();
  }, [applyRightPanelWidth, commitRightPanelWidth, getRightPanelResizeBounds, rightPanelResizable]);

  const handleRightPanelResizeKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!rightPanelResizable || !['ArrowLeft', 'ArrowRight', 'Home'].includes(event.key)) return;
    const panel = rightPanelRef.current;
    if (!panel) return;

    const currentWidth = panel.getBoundingClientRect().width;
    const defaultWidth = Number.parseFloat(rightPanelWidth ?? '') || rightPanelMinWidth;
    const nextWidth = event.key === 'Home'
      ? defaultWidth
      : currentWidth + (event.key === 'ArrowLeft' ? 16 : -16);
    commitRightPanelWidth(nextWidth);
    event.preventDefault();
  }, [commitRightPanelWidth, rightPanelMinWidth, rightPanelResizable, rightPanelWidth]);

  const resetRightPanelWidth = useCallback(() => {
    const defaultWidth = Number.parseFloat(rightPanelWidth ?? '') || rightPanelMinWidth;
    commitRightPanelWidth(defaultWidth);
  }, [commitRightPanelWidth, rightPanelMinWidth, rightPanelWidth]);

  useEffect(() => {
    if (!rightPanelResizable || !isRightPanelOpen) {
      rightPanelResizeCleanupRef.current?.();
    }
  }, [isRightPanelOpen, rightPanelResizable]);

  useLayoutEffect(() => {
    if (!bottomPanelResizable) {
      setResizedBottomPanelHeight(null);
      return;
    }

    if (!bottomPanelHeightStorageKey || typeof window === 'undefined') return;

    try {
      const storedHeight = Number.parseFloat(window.localStorage.getItem(bottomPanelHeightStorageKey) ?? '');
      bottomPanelManuallyResizedRef.current = Number.isFinite(storedHeight);
      setResizedBottomPanelHeight(
        Number.isFinite(storedHeight)
          ? Math.min(
              bottomPanelMaxHeight ?? Number.POSITIVE_INFINITY,
              Math.max(bottomPanelMinHeight, Math.round(storedHeight)),
            )
          : null,
      );
    } catch {
      // Storage can be unavailable in privacy-restricted browser contexts;
      // resizing still works for the current session.
      bottomPanelManuallyResizedRef.current = false;
      setResizedBottomPanelHeight(null);
    }
  }, [bottomPanelHeightStorageKey, bottomPanelMaxHeight, bottomPanelMinHeight, bottomPanelResizable]);

  const getBottomPanelResizeBounds = useCallback(() => {
    const panel = bottomPanelRef.current;
    const host = panel?.parentElement;
    const hostHeight = host?.getBoundingClientRect().height ?? 0;
    const rowGap = host ? Number.parseFloat(getComputedStyle(host).rowGap) || 0 : 0;
    return {
      min: bottomPanelMinHeight,
      max: Math.max(
        bottomPanelMinHeight,
        Math.min(
          bottomPanelMaxHeight ?? Number.POSITIVE_INFINITY,
          hostHeight - bottomPanelSiblingMinHeight - rowGap,
        ),
      ),
    };
  }, [bottomPanelMaxHeight, bottomPanelMinHeight, bottomPanelSiblingMinHeight]);

  const applyBottomPanelHeight = useCallback((height: number) => {
    const panel = bottomPanelRef.current;
    if (!panel) return;
    panel.style.height = `${Math.round(height)}px`;
  }, []);

  const getBottomPanelRowsFitHeight = useCallback(() => {
    const panel = bottomPanelRef.current;
    if (!panel) return null;

    const rows = Array.from(
      panel.querySelectorAll<HTMLElement>('#app-bottom-panel-content tbody > tr'),
    );
    if (rows.length === 0) return null;

    const panelTop = panel.getBoundingClientRect().top;
    const firstRow = rows[0];
    const scrollContainer = firstRow.closest<HTMLElement>('.overflow-auto');
    const firstRowTop = firstRow.getBoundingClientRect().top
      - panelTop
      + (scrollContainer?.scrollTop ?? 0);
    const footerHeight = panel
      .querySelector<HTMLElement>('[data-bottom-panel-footer]')
      ?.getBoundingClientRect().height ?? 0;
    const rowsHeight = rows.reduce(
      (total, row) => total + row.getBoundingClientRect().height,
      0,
    );
    const { min, max } = getBottomPanelResizeBounds();

    return Math.round(Math.min(
      max,
      Math.max(min, Math.ceil(firstRowTop + rowsHeight + footerHeight + 1)),
    ));
  }, [getBottomPanelResizeBounds]);

  const commitBottomPanelHeight = useCallback((height: number) => {
    const { min, max } = getBottomPanelResizeBounds();
    const nextHeight = Math.round(Math.min(max, Math.max(min, height)));
    applyBottomPanelHeight(nextHeight);
    setResizedBottomPanelHeight(nextHeight);

    if (bottomPanelHeightStorageKey && typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(bottomPanelHeightStorageKey, String(nextHeight));
      } catch {
        // The live height remains valid even when browser storage is blocked.
      }
    }
  }, [applyBottomPanelHeight, bottomPanelHeightStorageKey, getBottomPanelResizeBounds]);

  const handleBottomPanelResizeStart = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    if (!bottomPanelResizable || event.button !== 0) return;
    const panel = bottomPanelRef.current;
    if (!panel) return;

    bottomPanelResizeCleanupRef.current?.();
    bottomPanelAnimationRef.current?.cancel();
    bottomPanelAnimationRef.current = null;

    const startY = event.clientY;
    const startHeight = panel.getBoundingClientRect().height;
    const { min, max } = getBottomPanelResizeBounds();
    bottomPanelManuallyResizedRef.current = true;
    const previousCursor = document.body.style.cursor;
    const previousUserSelect = document.body.style.userSelect;
    let nextHeight = Math.min(max, Math.max(min, startHeight));

    const cleanup = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleEnd);
      window.removeEventListener('pointercancel', handleEnd);
      document.body.style.cursor = previousCursor;
      document.body.style.userSelect = previousUserSelect;
      panel.removeAttribute('data-resizing');
      bottomPanelResizeCleanupRef.current = null;
    };
    const handleMove = (pointerEvent: PointerEvent) => {
      nextHeight = getDraggedBottomPanelHeight(startHeight, startY, pointerEvent.clientY, min, max);
      applyBottomPanelHeight(nextHeight);
    };
    const handleEnd = () => {
      cleanup();
      commitBottomPanelHeight(nextHeight);
    };

    panel.setAttribute('data-resizing', 'true');
    document.body.style.cursor = 'row-resize';
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleEnd);
    window.addEventListener('pointercancel', handleEnd);
    bottomPanelResizeCleanupRef.current = cleanup;
    event.preventDefault();
  }, [applyBottomPanelHeight, bottomPanelResizable, commitBottomPanelHeight, getBottomPanelResizeBounds]);

  const handleBottomPanelResizeKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (!bottomPanelResizable || !['ArrowUp', 'ArrowDown'].includes(event.key)) return;
    const panel = bottomPanelRef.current;
    if (!panel) return;
    const currentHeight = panel.getBoundingClientRect().height;
    bottomPanelManuallyResizedRef.current = true;
    commitBottomPanelHeight(currentHeight + (event.key === 'ArrowUp' ? 16 : -16));
    event.preventDefault();
  }, [bottomPanelResizable, commitBottomPanelHeight]);

  const fitBottomPanelToRows = useCallback(() => {
    const fitHeight = getBottomPanelRowsFitHeight();
    if (fitHeight !== null) {
      bottomPanelManuallyResizedRef.current = true;
      commitBottomPanelHeight(fitHeight);
    }
  }, [commitBottomPanelHeight, getBottomPanelRowsFitHeight]);

  useLayoutEffect(() => {
    if (!bottomPanelAutoFitRows || !bottomPanelResizable || !isBottomPanelOpen) return;
    const panel = bottomPanelRef.current;
    if (!panel || typeof window === 'undefined') return;

    let frame = 0;
    let hasAppliedInitialFit = false;
    const scheduleFit = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        if (bottomPanelManuallyResizedRef.current) return;
        const fitHeight = getBottomPanelRowsFitHeight();
        if (fitHeight === null) return;
        const currentHeight = panel.getBoundingClientRect().height;

        // Always establish a compact initial size. Afterwards only remove
        // newly-created empty space. Manual resizing takes precedence over
        // subsequent table mutations and does not snap to resource rows.
        if (!hasAppliedInitialFit || currentHeight > fitHeight + 1) {
          commitBottomPanelHeight(fitHeight);
        }
        hasAppliedInitialFit = true;
      });
    };

    scheduleFit();
    const observer = new MutationObserver(scheduleFit);
    observer.observe(panel, { childList: true, subtree: true });

    return () => {
      window.cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [
    bottomPanelAutoFitRows,
    bottomPanelResizable,
    commitBottomPanelHeight,
    getBottomPanelRowsFitHeight,
    isBottomPanelOpen,
  ]);

  useEffect(() => {
    if (!bottomPanelResizable || !isBottomPanelOpen) {
      bottomPanelResizeCleanupRef.current?.();
    }
  }, [bottomPanelResizable, isBottomPanelOpen]);

  useLayoutEffect(() => {
    const panel = rightPanelRef.current;
    if (!panel || isMobile) return;

    const currentWidth = panel.getBoundingClientRect().width;
    rightPanelAnimationRef.current?.cancel();
    rightPanelAnimationRef.current = null;

    // Let the browser resolve the shared CSS width token (including calc/min
    // expressions) rather than duplicating its value in JavaScript.
    panel.style.width = isRightPanelOpen
      ? 'var(--qs-shell-right-panel-width, 380px)'
      : '0px';
    const targetWidth = panel.getBoundingClientRect().width;
    panel.style.width = `${currentWidth}px`;

    if (Math.abs(currentWidth - targetWidth) < 0.5) {
      panel.style.width = `${targetWidth}px`;
      if (!isRightPanelOpen) {
        rightPanelSnapshotRef.current = null;
        setStableRightPanel(null);
        setStableRightPanelDetached(false);
        setStableRightPanelWidth(undefined);
      }
      return;
    }

    const motion = getShellPanelMotionTiming(rightPanelMotionKind);

    if (motion.reducedMotion) {
      panel.style.width = `${targetWidth}px`;
      if (!isRightPanelOpen) {
        rightPanelSnapshotRef.current = null;
        setStableRightPanel(null);
        setStableRightPanelDetached(false);
        setStableRightPanelWidth(undefined);
      }
      return;
    }

    const animation = panel.animate(
      [
        { width: `${currentWidth}px` },
        { width: `${targetWidth}px` },
      ],
      {
        duration: panelMotionDurationMs ?? motion.duration,
        easing: motion.easing,
        fill: 'forwards',
      },
    );
    rightPanelAnimationRef.current = animation;

    animation.onfinish = () => {
      panel.style.width = isRightPanelOpen
        ? 'var(--qs-shell-right-panel-width, 380px)'
        : '0px';
      animation.cancel();
      if (rightPanelAnimationRef.current === animation) {
        rightPanelAnimationRef.current = null;
      }
      if (!isRightPanelOpen) {
        rightPanelSnapshotRef.current = null;
        setStableRightPanel(null);
        setStableRightPanelDetached(false);
        setStableRightPanelWidth(undefined);
      }
    };
  }, [effectiveRightPanelWidth, isMobile, isRightPanelOpen, panelMotionDurationMs, rightPanelMotionKind]);

  useLayoutEffect(() => {
    const panel = bottomPanelRef.current;
    if (!panel || isMobile) return;

    const currentHeight = panel.getBoundingClientRect().height;
    bottomPanelAnimationRef.current?.cancel();
    bottomPanelAnimationRef.current = null;

    panel.style.height = isBottomPanelOpen
      ? (resizedBottomPanelHeight === null ? 'auto' : `${resizedBottomPanelHeight}px`)
      : '0px';
    const targetHeight = panel.getBoundingClientRect().height;
    panel.style.height = `${currentHeight}px`;

    if (Math.abs(currentHeight - targetHeight) < 0.5) {
      panel.style.height = isBottomPanelOpen
        ? (resizedBottomPanelHeight === null ? 'auto' : `${resizedBottomPanelHeight}px`)
        : '0px';
      if (!isBottomPanelOpen) {
        bottomPanelSnapshotRef.current = null;
        setStableBottomPanel(null);
        setStableBottomPanelDetached(false);
      }
      return;
    }

    const motion = getShellPanelMotionTiming();
    if (motion.reducedMotion) {
      panel.style.height = isBottomPanelOpen
        ? (resizedBottomPanelHeight === null ? 'auto' : `${resizedBottomPanelHeight}px`)
        : '0px';
      if (!isBottomPanelOpen) {
        bottomPanelSnapshotRef.current = null;
        setStableBottomPanel(null);
        setStableBottomPanelDetached(false);
      }
      return;
    }

    const animation = panel.animate(
      [
        { height: `${currentHeight}px` },
        { height: `${targetHeight}px` },
      ],
      {
        duration: panelMotionDurationMs ?? motion.duration,
        easing: motion.easing,
        fill: 'forwards',
      },
    );
    bottomPanelAnimationRef.current = animation;

    animation.onfinish = () => {
      panel.style.height = isBottomPanelOpen
        ? (resizedBottomPanelHeight === null ? 'auto' : `${resizedBottomPanelHeight}px`)
        : '0px';
      animation.cancel();
      if (bottomPanelAnimationRef.current === animation) {
        bottomPanelAnimationRef.current = null;
      }
      if (!isBottomPanelOpen) {
        bottomPanelSnapshotRef.current = null;
        setStableBottomPanel(null);
        setStableBottomPanelDetached(false);
      }
    };
  }, [isBottomPanelOpen, isMobile, panelMotionDurationMs, resizedBottomPanelHeight]);

  const usesMaterialMobileChrome = isMobile && !nativeBridge.isNative;
  const shouldEnableShellPullToRefresh = usesMaterialMobileChrome && !isShellLocked;
  const shellPageKey = `${currentView}:${projectContext?.projectActiveTab ?? ''}`;
  const previousShellPageKeyRef = useRef(shellPageKey);
  useLayoutEffect(() => {
    if (previousShellPageKeyRef.current === shellPageKey) return;
    previousShellPageKeyRef.current = shellPageKey;

    const outlet = contentOutletRef.current;
    if (!outlet) return;
    outlet.scrollTop = 0;
    outlet.scrollLeft = 0;
  }, [shellPageKey]);
  const handleShellPullRefresh = useCallback(async () => {
    window.dispatchEvent(new CustomEvent(NATIVE_REFRESH_EVENT, {
      detail: { source: 'web-pull-to-refresh', ts: Date.now() },
    }));
    try {
      await DataService.refreshFromBackend(true);
    } catch (error) {
      console.error('Failed to refresh app data from pull gesture:', error);
    }
  }, []);
  // A narrow browser window keeps a desktop mouse even though the app is
  // presenting its phone shell. Give that surface touch-like drag scrolling;
  // actual iOS/Android runtimes continue to use the browser's native touch
  // scrolling path with no synthetic pointer handling.
  const enablesMouseDragScroll =
    isMobile && shellRuntime === 'browser' && !isShellLocked;
  const handleMouseDragScrollStart = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    if (!enablesMouseDragScroll || event.pointerType !== 'mouse' || event.button !== 0) return;
    const target = event.target as HTMLElement;
    const outlet = event.currentTarget;
    // Portal events bubble through React, but menus and dialogs are outside
    // this scroll surface and must keep their own pointer handling.
    if (!outlet.contains(target)) return;
    if (target.closest(
      'button, a, input, textarea, select, [role="button"], [role="link"], [role="menuitem"], [role="radio"], [role="tab"], [contenteditable="true"], [data-allow-text-selection]'
    )) return;
    mouseDragScrollRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      startScrollTop: outlet.scrollTop,
      axis: 'pending',
      outlet,
    };
    suppressMouseDragClickRef.current = false;
    window.getSelection()?.removeAllRanges();
    event.preventDefault();
    outlet.setPointerCapture(event.pointerId);
  }, [enablesMouseDragScroll]);
  const handleMouseDragScrollMove = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const drag = mouseDragScrollRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;
    if (drag.axis === 'pending' && Math.max(Math.abs(deltaX), Math.abs(deltaY)) >= 5) {
      drag.axis = Math.abs(deltaY) >= Math.abs(deltaX) ? 'vertical' : 'horizontal';
    }
    if (drag.axis !== 'vertical') return;

    event.preventDefault();
    drag.outlet.style.userSelect = 'none';
    drag.outlet.style.cursor = 'grabbing';
    drag.outlet.scrollTop = drag.startScrollTop - deltaY;
  }, []);
  const finishMouseDragScroll = useCallback((event: ReactPointerEvent<HTMLElement>) => {
    const drag = mouseDragScrollRef.current;
    if (!drag || drag.pointerId !== event.pointerId) return;

    if (drag.outlet.hasPointerCapture(event.pointerId)) {
      drag.outlet.releasePointerCapture(event.pointerId);
    }
    drag.outlet.style.removeProperty('user-select');
    drag.outlet.style.removeProperty('cursor');
    mouseDragScrollRef.current = null;

    if (drag.axis === 'vertical') {
      suppressMouseDragClickRef.current = true;
      if (suppressMouseDragClickTimerRef.current) {
        clearTimeout(suppressMouseDragClickTimerRef.current);
      }
      suppressMouseDragClickTimerRef.current = setTimeout(() => {
        suppressMouseDragClickRef.current = false;
        suppressMouseDragClickTimerRef.current = null;
      }, 0);
    }
  }, []);
  const handleMouseDragClickCapture = useCallback((event: ReactMouseEvent<HTMLElement>) => {
    if (!suppressMouseDragClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
  }, []);
  useEffect(() => () => {
    if (suppressMouseDragClickTimerRef.current) {
      clearTimeout(suppressMouseDragClickTimerRef.current);
    }
  }, []);
  const handleNavigate = onNavigate || onViewChange;
  const fallbackPaddingClass = getAppShellContentPaddingClass({
    noPadding,
    isShellLocked,
    preserveLockedPadding: true,
  });

  const nativeChrome = {
    isNative: nativeBridge.isNative,
    hasNativeTabBar: nativeBridge.hasNativeTabBar,
    hasNativeSidebar: nativeBridge.hasNativeSidebar,
    hasNativeHeader: nativeBridge.hasNativeHeader,
  };
  const preferNativeNavigation = shouldPreferNativeNavigation(nativeChrome);
  const preferNativeHeader = shouldPreferNativeHeader(nativeChrome);
  const shouldUseSidebarFrame = shouldUseFramelessAppShell({
    hasSidebarSlot: !!sidebar,
    nativeChrome,
  });
  // Trust the bridge's chrome signals, not the platform name: the macOS
  // webview shell reports hasNativeSidebar=false because the web app renders
  // its own navigation there. Shells that DO draw a native sidebar advertise
  // it via hasNativeSidebar / shouldHideWebNavigation.
  // Native shells draw their own navigation, including the iPhone shell's
  // push-aside project menu (IOSProjectSectionsMenu) — which is native because
  // what slides aside is the webview AND the native chrome above it. So the web
  // sidebar stays suppressed here at every width; the offcanvas drawer below it
  // serves mobile WEB only, where there is no native chrome to move.
  const shouldSuppressWebSidebarForNativeShell =
    nativeBridge.isNative &&
    (
      nativeBridge.shouldHideWebNavigation ||
      nativeBridge.hasNativeSidebar
    );
  const shouldRenderWebSidebar = !!sidebar && !shouldSuppressWebSidebarForNativeShell;
  const shouldSuppressWebBottomNavForNativeIOS =
    nativeBridge.isNative &&
    nativeBridge.platform === 'ios';
  const shouldRenderWebBottomNav =
    showBottomNav &&
    !hideBottomNav &&
    !preferNativeNavigation &&
    !shouldSuppressWebBottomNavForNativeIOS;
  // No live shell hosts the message/notification panels natively any more;
  // the macOS shell claims hasNativeSidebar but the panels are web-rendered.
  const shouldUseNativePanelHost = false;
  // The iOS web shell provides only a native BOTTOM tab bar — no native top
  // header — so the web must keep rendering its own header (and the safe-area
  // top inset that its floating-header clearance carries). Suppressing it here
  // (e.g. just because hasNativeTabBar) drops the safe-area term from the
  // floating clearance and slides content under the status bar. Only a native
  // top region (iPad native sidebar) should suppress the web header.
  const shouldSuppressWebHeaderForNativeIOS =
    nativeBridge.isNative &&
    nativeBridge.platform === 'ios' &&
    (nativeBridge.hasNativeSidebar || nativeBridge.hasNativeHeader);
  const nativeHeaderOffsetPx = Math.max(
    nativeBridge.viewportMetrics.topOffset,
    nativeBridge.headerMetrics.offset,
  );
  const nativeHeaderOffset = `${nativeHeaderOffsetPx}px`;
  const nativeIOSOwnsTopLayout =
    nativeBridge.isNative &&
    nativeBridge.platform === 'ios' &&
    nativeBridge.shouldHideWebNavigation &&
    nativeBridge.viewportMetrics.usesNativeSafeAreaLayout;
  const nativeIOSOwnsBottomLayout =
    nativeBridge.isNative &&
    nativeBridge.platform === 'ios' &&
    nativeBridge.hasNativeTabBar &&
    nativeBridge.viewportMetrics.usesNativeSafeAreaLayout;
  const nativeIPadStatusInsetPx =
    nativeBridge.isNative &&
    nativeBridge.platform === 'ios' &&
    nativeBridge.hasNativeSidebar
      ? 8
      : 0;
  const nativeShellTopOffset =
    nativeIPadStatusInsetPx > 0
      ? `calc(${nativeHeaderOffset} + ${nativeIPadStatusInsetPx}px)`
      : nativeHeaderOffset;

  // macOS shell: the native toolbar replaces the web header but is
  // transparent, so the web still paints the scroll-edge frost beneath it.
  const macShellOwnsHeader =
    nativeBridge.isNative &&
    nativeBridge.platform === 'macos' &&
    preferNativeHeader;
  // macOS shell: the native toolbar owns the header entirely. The web header
  // (floating pills for Messages, Notifications, Search) must not render —
  // those are published as native navButtons instead (see AuthenticatedController).
  const shouldRenderWebHeader =
    !!header &&
    !hideHeader &&
    !shouldSuppressWebHeaderForNativeIOS &&
    !macShellOwnsHeader;
  const usesCompactWebNavigationShell = isMobile || shouldRenderWebBottomNav;
  // Floating canvas header is now the default on every viewport — desktop
  // included. The canvas extends up to the top of the SidebarInset and the
  // header pills float over it with the iOS scroll-edge blur, mirroring
  // the mobile/tablet treatment so the chrome is consistent across
  // breakpoints. Previously this was gated to ≤1023px and desktop got the
  // solid brand-purple band instead.
  void usesCompactWebNavigationShell;
  const usesCompactHeaderLayout = true;
  // Floating canvas header is the default on every compact viewport. Each
  // page picks up the clearance via `--qs-floating-header-clearance` —
  // non-locked views inherit it automatically from `qs-shell-content-inner`
  // below; shell-locked views (project-detail, messages, calendar,
  // notifications) apply it on their internal scroll containers.
  const usesFloatingMobileDashboardHeader =
    usesCompactHeaderLayout &&
    shouldRenderWebHeader &&
    !!currentView;
  // The frost band only means anything behind a TRANSPARENT header. The
  // Material mobile app bar is a fully opaque `--header-surface-bg` covering
  // the same band, so the band can contribute nothing there — and on iOS it
  // actively breaks: a `position: fixed` `-webkit-backdrop-filter` layer
  // inside `.qs-shell-root` (which carries `will-change: transform` for the
  // keyboard counter-transform) gets promoted to its own compositing layer
  // and paints OVER the header despite its lower z-index. The band's feather
  // mask then reads as a white wash down the purple bar, strongest at the
  // status area. Chromium composites it correctly, so this only shows on the
  // installed iOS PWA.
  const rendersScrollEdgeFrost =
    usesFloatingMobileDashboardHeader && !usesMaterialMobileChrome;
  // Mobile/tablet: body padding stays 0 so content scrolls under the
  // floating header (iOS scroll-edge effect). Pages either auto-pad their
  // first row via `qs-shell-content-inner` (non-locked) or read
  // `--qs-floating-header-clearance` internally (locked).
  //
  // Desktop body offset is only required for SHELL-LOCKED views. They
  // can't scroll their content under the header (the outlet has
  // `overflow: hidden`) so the body has to reserve 66 px to keep the
  // first row visible below the floating chrome. SCROLL views, on the
  // other hand, *should* let content scroll under the header — that's
  // the iOS scroll-edge effect, and pages like Projects expect it.
  // Restricting the offset to locked-only on desktop fixes Projects: it
  // now starts below the header via `--qs-floating-header-clearance`
  // applied to `qs-shell-content-inner`, and on scroll the content
  // slides up behind the transparent header band.
  // `add-project` is locked but uses an INTERNAL scroll container
  // (`.apf-tab-scroll` inside the form's right pane). We want
  // scroll-edge behaviour on it — content slides under the
  // transparent header — so the body offset must NOT push the whole
  // form down below the header. The form opts out of the body offset
  // and instead reads the clearance var on its inner content.
  const isAddProjectFloatingScrollEdge = currentView === 'add-project';
  // Takeoff is a canvas-driven view that should fill the full viewport from
  // top to bottom, with the floating main header overlapping the upper strip.
  // Without this exception, the body's 66px padding-top reserves a band that
  // crops the takeoff's top → side panels + drawing canvas don't fill the
  // available height. Same logical exception as add-project.
  const isProjectTakeoffFloatingScrollEdge =
    currentView === 'project-detail' && projectContext?.projectActiveTab === 'takeoff';
  // BOQ importer is a full-canvas overlay inside project-detail. Same
  // exception as takeoff — body offset would push the importer canvas
  // down by 66px and prevent content scrolling up behind the header.
  const isProjectImportBOQFloatingScrollEdge =
    currentView === 'project-detail' && projectContext?.projectActiveTab === 'import-boq';
  // Contacts is a locked split view (list + profile panel with internal scroll)
  // but should still get the iOS scroll-edge — the list slides under the
  // transparent header. Same opt-out-of-body-offset pattern as add-project /
  // takeoff: the body reserves no top band, and the list applies the header
  // clearance on its own scroll container.
  const isContactsFloatingScrollEdge = currentView === 'contacts';
  // Admin (project settings) is a browse-style scrolling form — it should slide
  // under the floating header like Overview, not reserve the 66px band.
  const isProjectAdminFloatingScrollEdge =
    currentView === 'project-detail' && isProjectAdminTab(projectContext?.projectActiveTab);
  const floatingHeaderRequiresBodyOffset =
    shellPlatform === 'desktop' &&
    isShellLocked &&
    !isAddProjectFloatingScrollEdge &&
    !isProjectTakeoffFloatingScrollEdge &&
    !isProjectImportBOQFloatingScrollEdge &&
    !isContactsFloatingScrollEdge &&
    !isProjectAdminFloatingScrollEdge;
  const usesNativeViewportContract = nativeBridge.viewportMetrics.usesNativeSafeAreaLayout;
  const shellTopOffset =
    nativeIOSOwnsTopLayout
      ? '0px'
      : usesNativeViewportContract
      ? nativeShellTopOffset
      : preferNativeHeader
        ? nativeShellTopOffset
        // The borderless Windows (Tauri) shell paints a fixed title bar; its
        // inset (0px everywhere else) must fold into the offset the shell root
        // emits inline, otherwise that inline value shadows the html-level
        // override and the header/body render under the title bar.
        : 'calc(var(--safe-area-top) + var(--quantis-win-titlebar-inset, 0px))';
  const shellBottomOffset =
    usesNativeViewportContract
      ? `${nativeBridge.viewportMetrics.bottomOffset}px`
      : nativeBridge.hasNativeTabBar
        ? 'var(--quantis-native-tab-bar-offset, 0px)'
        : '0px';
  // Total reserved nav height = the bar's content zone + the home-indicator
  // floor its background fills + the mobile-browser chrome the whole bar is
  // lifted by. Composed in one pure place (mobileBottomNavGeometry) that also
  // owns the shell's overrides of the theme.css defaults, so clearance can't
  // drift from the bar's real footprint. MobileBottomNav lays itself out from
  // the same vars.
  const bottomNavGeometry = composeMobileBottomNavGeometry({
    isRendered: shouldRenderWebBottomNav,
    usesMaterialChrome: usesMaterialMobileChrome,
    shellRuntime,
  });
  const mobileBottomNavHeight = bottomNavGeometry.height;
  // Surfaces that portal to document.body (the toaster) sit OUTSIDE this shell
  // subtree, so the geometry overrides carried on the shell root are invisible
  // to them: the mobile snackbar composed a 55px + 34px bar from the :root
  // defaults while the real one is 80px + 34px, and drew itself over the tab
  // row. Republish the composed height on the document element so an
  // out-of-tree surface reads the bar's true footprint, 0px included.
  useEffect(() => {
    const root = document.documentElement;
    root.style.setProperty(MOBILE_BOTTOM_NAV_HEIGHT_VAR, mobileBottomNavHeight);
    return () => {
      root.style.removeProperty(MOBILE_BOTTOM_NAV_HEIGHT_VAR);
    };
  }, [mobileBottomNavHeight]);
  const compactBottomClearance = shouldRenderWebBottomNav
    ? 'var(--qs-mobile-bottom-nav-height)'
    : '0px';
  const roomyBottomClearance = shouldRenderWebBottomNav
    ? 'calc(var(--qs-mobile-bottom-nav-height) + var(--space-md))'
    : '0px';
  // iOS native bar and the macOS shell's transparent toolbar both want the
  // underlap pattern: scroll outlet fills to the viewport top, clearance
  // lives inside the scroll content, rows slide under the native chrome.
  const shouldUnderlapNativeHeader =
    nativeBridge.isNative &&
    (nativeBridge.platform === 'ios' || nativeBridge.platform === 'macos') &&
    !shouldRenderWebHeader;
  // Messages is a shell-locked view that owns its own scroll. To make the chat
  // and list rows slide BEHIND the translucent native nav bar (the iOS look),
  // the shared inner wrapper must NOT push it down; instead Messages reserves
  // the nav-bar clearance inside each of its own scroll containers via the
  // --qs-messages-top-clearance var below. Scoped to Messages so the other
  // shell-locked views (calendar, notifications, project detail) are untouched.
  const isNativeMessagesUnderlap =
    shouldUnderlapNativeHeader && isShellLocked && currentView === 'messages';
  const contentTopOffset = shouldRenderWebHeader ? '0px' : 'var(--qs-shell-top-offset)';
  const effectiveContentTopOffset =
    nativeIOSOwnsTopLayout
      ? hideHeader
        ? `${nativeBridge.viewportMetrics.topOffset}px`
        : '0px'
      : contentTopOffset;
  const effectiveStickyTopOffset = effectiveContentTopOffset;
  const nativeHeaderClearance =
    shouldSuppressWebHeaderForNativeIOS
      ? nativeHeaderOffset
      : '0px';
  // Canvas-view top clearance: enough room for the floating header pills
  // (54 px section + 12 px header paddingTop = 66 px) PLUS the system
  // safe-area (status bar / Dynamic Island).
  //
  // Mobile/tablet floating: body paddingTop is 0, so each consuming view
  // adds this clearance internally and content scrolls under the header
  // with the iOS scroll-edge effect.
  //
  // Desktop floating: body already pads the layout by the same amount
  // (see `floatingHeaderRequiresBodyOffset` above) since most desktop
  // pages were authored against the old solid-header body padding and
  // don't read the clearance var themselves. If the var also expanded to
  // 66 px+safe-area, every page that DOES read it (ChatListPanel,
  // ProjectDetail, ProjectOverviewTab, CalendarPage) would double-pad —
  // ~130 px of empty space above the first row. So we expose 0 px on
  // desktop and let the body padding own the offset.
  //
  // Native iOS uses Apple's standard pattern: the WebView's
  // `scrollView.contentInset.top` reserves the chrome height directly
  // (mirrors `UINavigationStack`). Adding CSS padding on top of that
  // would double-offset the page, so we leave the var at 0 on native
  // and let the iOS shell own the offset.
  // Desktop: scroll views need a real clearance value so their first
  // row starts below the floating header (via `padding-top` on
  // `qs-shell-content-inner`) and only slides under it as the user
  // scrolls — that's the iOS scroll-edge feel. Locked views still get
  // 0 because `floatingHeaderRequiresBodyOffset` already reserves the
  // 66 px band on the body for them.
  // Mobile/tablet: always reserve via the var; the body padding stays
  // 0 so content can scroll under the header from the moment the page
  // loads.
  // Native iOS shell: the bar belongs to the native NavigationStack
  // (system Liquid Glass), and `nativeHeaderClearance` already holds
  // the correct offset broadcast by the bridge. Without this branch
  // the variable was hard-coded to '0px' whenever the web header was
  // suppressed, so every page rendered its first row under the
  // native bar.
  const floatingHeaderClearance = shouldSuppressWebHeaderForNativeIOS
    ? nativeHeaderClearance
    : usesFloatingMobileDashboardHeader
      ? shellPlatform === 'desktop'
        ? isShellLocked && !isAddProjectFloatingScrollEdge && !isProjectTakeoffFloatingScrollEdge && !isProjectImportBOQFloatingScrollEdge && !isContactsFloatingScrollEdge && !isProjectAdminFloatingScrollEdge
          ? '0px'
          // import-boq removes the body offset and manages its own scroll container,
          // so it needs the full header height (shell-top-offset + band) as clearance.
          : isProjectImportBOQFloatingScrollEdge
            ? 'calc(var(--qs-shell-top-offset, 0px) + var(--qs-floating-header-band))'
            : 'var(--qs-floating-header-band)'
        // Mobile: the compact band — the pill row is ~44px, and the full
        // 66px band left a dead strip above the large title.
        : 'calc(var(--safe-area-top, 0px) + var(--qs-floating-header-band-compact, 50px))'
      : '0px';
  const shellRootStyle = {
    '--qs-shell-top-offset': shellTopOffset,
    '--qs-shell-bottom-offset': shellBottomOffset,
    '--qs-content-top-offset': effectiveContentTopOffset,
    '--qs-sticky-top-offset': effectiveStickyTopOffset,
    '--qs-native-header-clearance': nativeHeaderClearance,
    '--qs-floating-header-clearance': floatingHeaderClearance,
    '--qs-web-header-height': 'var(--header-height)',
    // Every web-rendered mobile surface uses the same Material shell geometry,
    // including installed PWAs and responsive browser mode. Native shells keep
    // ownership of their own navigation chrome.
    '--qs-floating-header-band-compact': usesMaterialMobileChrome ? '64px' : undefined,
    // The iPhone home-indicator clearance survives an installed PWA reporting
    // env(safe-area-inset-bottom) as zero; see mobileBottomNavGeometry.
    '--qs-mobile-bottom-nav-min-height': bottomNavGeometry.minHeight,
    '--qs-mobile-bottom-nav-vertical-padding': bottomNavGeometry.verticalPadding,
    '--qs-mobile-bottom-nav-safe-floor': bottomNavGeometry.safeFloor,
    '--qs-mobile-bottom-nav-height': mobileBottomNavHeight,
    '--qs-bottom-clearance-compact': compactBottomClearance,
    '--qs-bottom-clearance-roomy': roomyBottomClearance,
    '--qs-scroll-tail-space':
      nativeIOSOwnsBottomLayout
        ? '0px'
        // Dashboard and Projects self-reserve the fixed bottom-nav clearance
        // inside their page content. Adding the generic 8rem scroll tail as
        // well would stack two navigation clearances and let the user scroll
        // beyond the final card/row into a large empty band.
        : shouldRenderWebBottomNav && currentView !== 'dashboard' && currentView !== 'projects'
          ? '8rem'
          : '0px',
  } as CSSProperties;
  const contentOutletStyle = {
    // When the web header is hidden (hideHeader=true — mobile messages thread) the
    // body already carries var(--qs-shell-top-offset) as its own paddingTop, and
    // --qs-content-top-offset resolves to the same value (safe-area-top), so
    // applying both would double-count the safe-area (2×62px = 124px on iPhone).
    // The ChatHeader handles safe-area internally, so we skip it here — same
    // pattern as shouldUnderlapNativeHeader.
    paddingTop: (shouldUnderlapNativeHeader || hideHeader) ? '0px' : 'var(--qs-content-top-offset)',
    scrollPaddingTop: 'var(--qs-content-top-offset)',
  };
  // Auto-clearance for the floating canvas header. Non-locked views use the
  // shell's own scroll outlet, so the clearance lives on `qs-shell-content-inner`
  // — content-inner moves up on scroll, padding goes with it, and the page's
  // first row slides under the transparent header. Shell-locked views
  // (project-detail, messages, calendar, notifications) own their internal
  // scroll and apply the clearance on their own inner scroll container.
  const shouldAutoApplyFloatingClearance =
    usesFloatingMobileDashboardHeader && !isShellLocked;
  const contentInnerStyle = isNativeMessagesUnderlap
    ? ({
        // Messages fills to the viewport top (rows scroll behind the nav bar);
        // each of its scroll containers reserves the clearance itself.
        paddingTop: '0px',
        ['--qs-messages-top-clearance' as string]: 'var(--qs-content-top-offset)',
      } as CSSProperties)
    : shouldUnderlapNativeHeader
    ? ({ paddingTop: 'var(--qs-content-top-offset)' } as CSSProperties)
    : shouldAutoApplyFloatingClearance
      ? ({ paddingTop: 'calc(var(--qs-floating-header-clearance, 0px) + var(--qs-large-title-clearance, 0px))' } as CSSProperties)
      : undefined;

  // Route/content failures should not take the application controls with
  // them. The outer provider boundary still protects startup and shell-level
  // failures; this inner boundary contains ordinary page crashes inside the
  // content outlet so header, sidebar and mobile navigation remain usable.
  const contentBoundaryKey = typeof window === 'undefined'
    ? currentView
    : `${currentView ?? ''}:${window.location.pathname}${window.location.search}`;
  const guardedChildren = (
    <ErrorBoundary resetKey={contentBoundaryKey} surface="content">
      {children}
    </ErrorBoundary>
  );

  if (shouldUseSidebarFrame) {
    return (
      <SidebarProvider style={{ '--sidebar-width': APP_SHELL_DIMENSIONS.sidebarWidth } as any}>
        <section
          data-slot="app-root"
          data-shell-runtime={shellRuntime}
          data-shell-interface={usesMaterialMobileChrome ? 'material' : shellRuntime}
          data-native-platform={nativeBridge.platform}
          data-control-surface={preferNativeNavigation ? 'native' : 'web'}
          data-shell-platform={shellPlatform}
          data-shell-scroll={resolvedScrollMode}
          data-shell-preset={layoutPreset}
          data-vaul-drawer-wrapper=""
          // Layout-shell purple kill-switch: `.qs-shell-root--with-sidebar`
          // (layout-system.css:218) and `.qs-shell-root--with-sidebar
          // .qs-shell-body` (line 373) both paint `var(--system-navigation)`
          // unconditionally. On floating-canvas views the transparent
          // header lets that purple bleed through the safe-area+66px band.
          // Override at the root so every descendant inherits canvas.
          // Also apply when hideHeader is true — full-screen content views
          // like the mobile chat thread suppress the web header entirely,
          // so the floating-header condition is false. Without this
          // branch, the qs-shell-frame's brand-purple shows through the
          // translucent iOS status bar in standalone PWA mode.
          style={{
            ...shellRootStyle,
            ...(usesFloatingMobileDashboardHeader || hideHeader
              ? { background: 'var(--canvas)' }
              : null),
          }}
          className={cn(
            'qs-shell-root qs-shell-root--with-sidebar font-apple',
          )}
          aria-label="Application Shell"
        >
          <div data-slot="app-card-layer" className="qs-shell-card-layer">
          {shouldRenderWebSidebar ? sidebar : null}

          <SidebarInset
            data-slot="app-frame"
            className="qs-shell-frame"
            style={
              usesFloatingMobileDashboardHeader || hideHeader
                ? { background: 'var(--canvas)' }
                : undefined
            }
          >
            {/* Toolbar — floats over the content column only (not the sidebar).
                Desktop: transparent at rest. Mobile: absolute overlay,
                click-through. */}
            {shouldRenderWebHeader && (
              <header
                id="app-header-container"
                data-slot="app-header"
                className={cn(
                  'qs-shell-header',
                  !preferNativeHeader && 'qs-shell-header--bordered'
                )}
                style={usesCompactHeaderLayout ? (
                  shellPlatform === 'desktop' ? {
                    // Desktop: toolbar sits at the very top of the inset column.
                    // Transparent at rest; ScrollEdgeBlur provides the frost.
                    paddingTop: 'calc(var(--qs-shell-top-offset, 0px) + 12px)',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    pointerEvents: usesFloatingMobileDashboardHeader ? 'none' : undefined,
                    background: 'transparent',
                    backdropFilter: 'none',
                    WebkitBackdropFilter: 'none',
                  } : {
                    // Material mobile app bars attach to the top edge. The
                    // shell offset still preserves a real status-bar safe area
                    // in installed PWAs; the old extra 12px belonged to the
                    // floating-glass treatment and left a visible white gap.
                    paddingTop: usesMaterialMobileChrome
                      ? 'var(--qs-shell-top-offset, 0px)'
                      : 'calc(var(--qs-shell-top-offset, 0px) + 12px)',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    pointerEvents: usesFloatingMobileDashboardHeader ? 'none' : undefined,
                    // The outer header owns the iOS safe-area padding. When
                    // the Material app bar itself is purple but this wrapper
                    // stays transparent, the canvas shows through above it as
                    // a separate white status-bar banner. Paint the wrapper
                    // with the same surface so the chrome is one continuous
                    // block; the row still starts below the safe area.
                    background: usesMaterialMobileChrome
                      ? 'var(--header-surface-bg)'
                      : usesFloatingMobileDashboardHeader
                        ? 'transparent'
                        : 'var(--glass-bg-strong)',
                    // Apply elevation to the complete app bar, including its
                    // safe-area fill. Putting this shadow on the inner row
                    // draws a dark seam exactly where the status area meets
                    // the toolbar on iOS.
                    boxShadow: usesMaterialMobileChrome
                      ? '0 2px 5px color-mix(in srgb, black 13%, transparent), 0 10px 24px -14px color-mix(in srgb, black 34%, transparent)'
                      : undefined,
                    backdropFilter: usesMaterialMobileChrome || usesFloatingMobileDashboardHeader
                      ? 'none'
                      : 'blur(20px) saturate(180%)',
                    WebkitBackdropFilter: usesMaterialMobileChrome || usesFloatingMobileDashboardHeader
                      ? 'none'
                      : 'blur(20px) saturate(180%)',
                  }
                ) : {
                  paddingTop: 'var(--qs-shell-top-offset)',
                  background: 'var(--header-surface-bg)',
                }}
              >
                {header}
              </header>
            )}

            {/* Scroll-edge blur — spans the FULL window width, across the
                sidebar as well as the content column, the way a real window
                toolbar does. It lives inside the content column in the tree,
                so `absolute` would start it at the sidebar's right edge and
                leave the leading ~228px unfrosted; `fixed` detaches it from
                that box and pins it to the shell instead.
                On mobile the sidebar is hidden, so this changes nothing there.
                Note the containing block: `app-root` carries
                data-vaul-drawer-wrapper, and vaul sets a transform on it while
                a drawer is open, which makes it the containing block for this
                fixed child. That is what we want — the band scales back with
                the rest of the shell instead of staying pinned to the glass.
                app-root is the full window box in every shell (macos-shell.css
                and win-shell.css only retint it, never inset it), so the band
                is full-bleed either way the containing block resolves.
                The macOS shell hides the web header but its native toolbar is
                transparent glass, so the same frost band renders beneath it
                (sized to the native top offset the shell injects). */}
            {(rendersScrollEdgeFrost || macShellOwnsHeader) && (
              <ScrollEdgeBlur
                aria-hidden="true"
                absolute
                height={
                  macShellOwnsHeader
                    ? 'var(--quantis-native-content-top-offset, 52px)'
                    : shellPlatform === 'desktop'
                      // Was --qs-toolbar-height (52px), a legacy toolbar value
                      // unrelated to the floating header, which left the frost
                      // 24px shorter than the chrome it sits behind — rows
                      // crossed that strip completely un-blurred and read as
                      // crisp text over the header.
                      // Mirrors the header's OWN box above: it pads by
                      // shell-top-offset + 12px and then holds the band. Keep
                      // the 12px in step with that paddingTop if it changes.
                      // Resolves ~2px taller than the header, which the mask
                      // feathers away; under-covering is what shows text.
                      ? 'calc(var(--qs-shell-top-offset, 0px) + 12px + var(--qs-floating-header-band))'
                      : 'var(--qs-floating-header-clearance, 0px)'
                }
                style={{ zIndex: 49, position: 'fixed', top: 0, left: 0, right: 0 }}
              />
            )}

            <section
              data-slot="app-body"
              data-document-workspace={currentView === 'project-detail' && projectContext?.projectActiveTab === 'documents' || undefined}
              className="qs-shell-body"
              data-panel-open={isRightPanelOpen && !isMobile && !shouldUseNativePanelHost}
              aria-label="Application Body"
              style={usesCompactHeaderLayout ? {
                // Floating-header views: body has no header clearance — the
                // page fills the top and content slides through the
                // transparent header on scroll. The page itself adds its own
                // initial top spacing so the first row isn't hidden.
                // Other views push content below the solid header height.
                // Floating-header canvas views: body has 0 paddingTop so the
                // scroll container fills to the very top of the viewport.
                // Content slides up through the (transparent) floating header
                // AND under the system status bar / Dynamic Island for the
                // full-screen native feel. Each canvas view supplies its own
                // safe-area-top + floating-header-clearance internally so the
                // first row still sits below the chrome on initial render.
                // Project-detail keeps the explicit body offset (its tab bar
                // is content-anchored, not floating).
                paddingTop: usesFloatingMobileDashboardHeader
                  ? (
                      floatingHeaderRequiresBodyOffset
                        ? 'calc(var(--qs-shell-top-offset, 0px) + var(--qs-floating-header-band))'
                        : '0px'
                    )
                  : (shouldRenderWebHeader
                      ? 'calc(var(--qs-shell-top-offset) + var(--qs-floating-header-band))'
                      // Underlap shells keep the body at 0 — the clearance is
                      // applied inside the scroll content (contentInnerStyle),
                      // otherwise the offset would be added twice.
                      // hideHeader (mobile messages thread): ChatHeader handles
                      // safe-area internally, so body must also be 0 — same
                      // pattern as native underlap.
                      : (shouldUnderlapNativeHeader || hideHeader)
                        ? '0px'
                        : 'var(--qs-shell-top-offset, 0px)'),
                paddingBottom:
                  currentView === 'project-detail' && shouldRenderWebBottomNav
                    ? 'var(--qs-mobile-bottom-nav-height)'
                    : undefined,
                ...(usesFloatingMobileDashboardHeader ? { background: 'var(--canvas)' } : null),
              } : undefined}
            >
              {/* Inner row — the complete main canvas + right panel side-by-side. */}
              <div
                className="qs-shell-body-row"
                data-left-panel-open={!isMobile && isLeftPanelOpen || undefined}
                data-detached-left-inspector={!isMobile && isLeftPanelOpen || undefined}
                data-detached-inspector={
                  (isRightPanelOpen ? rightPanelDetached : stableRightPanelDetached) || undefined
                }
                data-detached-bottom-panel={
                  (isBottomPanelOpen ? bottomPanelDetached : stableBottomPanelDetached) || undefined
                }
                data-split-inspector={(!isMobile && isRightPanelOpen && rightPanelSplit) || undefined}
              >
                {!isMobile && <ShellLeftInspector side="left" open docks minWidth={240} maxWidth={360} siblingMinWidth={360 + (isRightPanelOpen ? Number.parseFloat(effectiveRightPanelWidth || '') || 400 : 0)} onWidthChange={setLeftPanelWidth} className={isLeftPanelOpen ? 'qs-shell-left-panel' : 'qs-shell-left-panel !hidden'}>
                  <div id="app-left-panel-portal" data-slot="app-left-panel" className="qs-shell-left-panel-content h-full min-h-0 flex flex-col" />
                </ShellLeftInspector>}
                <div className="qs-shell-main-column">
                  {/* Main content area */}
                  <main
                    id="app-main-layout"
                    data-slot="app-content-view"
                    className="qs-shell-content-view"
                  >
                    <section
                      ref={contentOutletRef}
                      className={cn(
                        'qs-shell-content-outlet',
                        isShellLocked
                          ? 'qs-shell-content-outlet--locked'
                          : 'qs-shell-content-outlet--scroll'
                      )}
                      style={contentOutletStyle}
                      data-slot="app-content-outlet"
                      data-mouse-drag-scroll={enablesMouseDragScroll || undefined}
                      aria-label="Content Outlet"
                      onPointerDown={handleMouseDragScrollStart}
                      onPointerMove={handleMouseDragScrollMove}
                      onPointerUp={finishMouseDragScroll}
                      onPointerCancel={finishMouseDragScroll}
                      onClickCapture={handleMouseDragClickCapture}
                    >
                      {shouldEnableShellPullToRefresh && (
                        <ShellPullToRefresh
                          scrollContainerRef={contentOutletRef}
                          onRefresh={handleShellPullRefresh}
                          enableMouseGesture={enablesMouseDragScroll}
                        />
                      )}
                      <div
                        className={cn(
                          'qs-shell-content-inner',
                          isShellLocked
                            ? 'qs-shell-content-inner--full-height'
                            : 'qs-shell-content-inner--fill',
                          contentPaddingClass
                        )}
                        style={contentInnerStyle}
                      >
                        {guardedChildren}
                        {!isShellLocked && (
                          <div className="qs-shell-bottom-spacer" aria-hidden="true" />
                        )}
                      </div>
                    </section>
                  </main>

                </div>

                {/* Desktop Right Panel */}
                {!isMobile && !shouldUseNativePanelHost && (
                  <div
                    ref={rightPanelRef}
                    data-slot="app-right-panel"
                    data-open={isRightPanelOpen}
                    className="qs-shell-right-panel"
                    style={effectiveRightPanelWidth
                      ? ({ '--qs-shell-right-panel-width': effectiveRightPanelWidth } as CSSProperties)
                      : undefined}
                  >
                    {isRightPanelOpen && rightPanelResizable && (
                      <div
                        role="separator"
                        aria-label="Resize inspector panel"
                        aria-orientation="vertical"
                        aria-valuemin={rightPanelMinWidth}
                        aria-valuemax={rightPanelMaxWidth}
                        aria-valuenow={resizedRightPanelWidth ?? (Number.parseFloat(rightPanelWidth ?? '') || rightPanelMinWidth)}
                        tabIndex={0}
                        className="qs-shell-right-panel-resizer"
                        title="Drag to resize. Double-click to reset."
                        onPointerDown={handleRightPanelResizeStart}
                        onKeyDown={handleRightPanelResizeKeyDown}
                        onDoubleClick={resetRightPanelWidth}
                      />
                    )}
                    <div
                      id="app-right-panel-portal"
                      data-slot="app-right-panel-content"
                      className="qs-shell-right-panel-content"
                    >
                      {isRightPanelOpen
                        ? (rightPanel ?? rightPanelSnapshotRef.current)
                        : (stableRightPanel ?? rightPanelSnapshotRef.current)}
                    </div>
                  </div>
                )}

                {/* Detached data sheets occupy only the main column, leaving
                    the right inspector available at the workspace's full height. */}
                {!isMobile && (
                  <div
                    ref={bottomPanelRef}
                    id="app-bottom-panel-portal"
                    data-slot="app-bottom-panel"
                    data-open={isBottomPanelOpen}
                    data-resizable={bottomPanelResizable || undefined}
                    className="qs-shell-bottom-panel"
                  >
                    {isBottomPanelOpen && bottomPanelResizable && (
                      <div
                        role="separator"
                        aria-label="Resize data sheet panel"
                        aria-orientation="horizontal"
                        aria-valuemin={bottomPanelMinHeight}
                        aria-valuemax={getBottomPanelResizeBounds().max}
                        aria-valuenow={resizedBottomPanelHeight ?? undefined}
                        tabIndex={0}
                        className="qs-shell-bottom-panel-resizer"
                        title="Drag to show more or fewer resource rows. Double-click to fit all rows."
                        onPointerDown={handleBottomPanelResizeStart}
                        onKeyDown={handleBottomPanelResizeKeyDown}
                        onDoubleClick={fitBottomPanelToRows}
                      />
                    )}
                    <div id="app-bottom-panel-content" className="qs-shell-bottom-panel-content">
                      {isBottomPanelOpen
                        ? (bottomPanel ?? bottomPanelSnapshotRef.current)
                        : (stableBottomPanel ?? bottomPanelSnapshotRef.current)}
                    </div>
                  </div>
                )}
              </div>

            </section>
          </SidebarInset>

          {shouldRenderWebBottomNav && handleNavigate && (
            <MobileBottomNav
              user={user}
              currentView={currentView}
              onNavigate={handleNavigate}
              onLogout={onLogout || (() => {})}
              shellRuntime={shellRuntime}
              nativePlatform={nativeBridge.platform}
              isNative={nativeBridge.isNative}
            />
          )}

          {isMobile && !shouldUseNativePanelHost && rightPanel && mobileRightPanelMode === 'drawer' && (
            <MobileInspectorScreen open={isRightPanelOpen} onClose={onCloseRightPanel}>
              {rightPanel}
            </MobileInspectorScreen>
          )}
          </div>
        </section>
      </SidebarProvider>
    );
  }

  // Fallback layout without sidebar
  return (
    <section
      data-slot="app-root"
      data-shell-runtime={shellRuntime}
      data-shell-interface={usesMaterialMobileChrome ? 'material' : shellRuntime}
      data-native-platform={nativeBridge.platform}
      data-control-surface={preferNativeNavigation ? 'native' : 'web'}
      data-shell-platform={shellPlatform}
      data-shell-scroll={resolvedScrollMode}
      data-shell-preset={layoutPreset}
      data-vaul-drawer-wrapper=""
      style={shellRootStyle}
      className={cn(
        'qs-shell-root qs-shell-root--fallback font-apple',
      )}
      aria-label="Application Shell"
    >
      {/* Scroll-edge blur band (fallback layout) — same iOS feathered
          blur the sidebar layout uses, so every header gets it. */}
      {rendersScrollEdgeFrost && (
        <ScrollEdgeBlur
          aria-hidden="true"
          absolute
          height="var(--qs-floating-header-clearance, 0px)"
          style={{ zIndex: 49 }}
        />
      )}
      {shouldRenderWebHeader && (
        <header
          id="app-header-container"
          data-slot="app-header"
          className={cn(
            'qs-shell-header',
            !preferNativeHeader && 'qs-shell-header--bordered'
          )}
          style={usesCompactHeaderLayout ? {
            paddingTop: usesMaterialMobileChrome
              ? 'var(--qs-shell-top-offset, 0px)'
              : 'calc(var(--qs-shell-top-offset, 0px) + 12px)',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 50,
            // Keep the physical-device safe area visually attached to the
            // Material app bar. This fallback branch is used on surfaces that
            // do not render the main sidebar frame, so it must follow the same
            // rule as the primary shell above.
            background: usesMaterialMobileChrome
              ? 'var(--header-surface-bg)'
              : usesFloatingMobileDashboardHeader
                ? 'transparent'
                : 'var(--glass-bg-strong)',
            boxShadow: usesMaterialMobileChrome
              ? '0 2px 5px color-mix(in srgb, black 13%, transparent), 0 10px 24px -14px color-mix(in srgb, black 34%, transparent)'
              : undefined,
            backdropFilter: usesMaterialMobileChrome || usesFloatingMobileDashboardHeader
              ? 'none'
              : 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: usesMaterialMobileChrome || usesFloatingMobileDashboardHeader
              ? 'none'
              : 'blur(20px) saturate(180%)',
          } : {
            paddingTop: 'var(--qs-shell-top-offset)',
          }}
        >
          {header}
        </header>
      )}

      <section
        data-slot="app-body"
        className="qs-shell-body"
        aria-label="Application Body"
        style={usesCompactHeaderLayout ? {
          // Mirror the sidebar branch (see qs-shell-body above): when the
          // floating canvas header is on and the layout doesn't reserve a
          // body offset, padding-top is 0 so content can slide under the
          // (transparent) header. The fallback branch previously left
          // safe-area-top on the body, double-padding pages that already
          // read --qs-floating-header-clearance internally.
          paddingTop: usesFloatingMobileDashboardHeader
            ? (
                floatingHeaderRequiresBodyOffset
                  ? 'calc(var(--qs-shell-top-offset, 0px) + var(--qs-floating-header-band))'
                  : '0px'
              )
            : (shouldRenderWebHeader
                ? 'calc(var(--qs-shell-top-offset) + var(--qs-floating-header-band))'
                : 'var(--qs-shell-top-offset, 0px)'),
          paddingBottom:
            currentView === 'project-detail' && shouldRenderWebBottomNav
              ? 'var(--qs-mobile-bottom-nav-height)'
              : undefined,
          ...(usesFloatingMobileDashboardHeader ? { background: 'var(--canvas)' } : null),
        } : undefined}
      >
        <main
          id="app-main-layout"
          data-slot="app-content-view"
          className="qs-shell-content-view"
        >
          <section
            ref={contentOutletRef}
            className={cn(
              'qs-shell-content-outlet',
              'qs-shell-content-outlet--surface',
              isShellLocked
                ? 'qs-shell-content-outlet--locked'
                : 'qs-shell-content-outlet--scroll'
            )}
            style={contentOutletStyle}
            data-slot="app-content-outlet"
            data-mouse-drag-scroll={enablesMouseDragScroll || undefined}
            onPointerDown={handleMouseDragScrollStart}
            onPointerMove={handleMouseDragScrollMove}
            onPointerUp={finishMouseDragScroll}
            onPointerCancel={finishMouseDragScroll}
            onClickCapture={handleMouseDragClickCapture}
          >
            {shouldEnableShellPullToRefresh && (
              <ShellPullToRefresh
                scrollContainerRef={contentOutletRef}
                onRefresh={handleShellPullRefresh}
                enableMouseGesture={enablesMouseDragScroll}
              />
            )}
            <div
              className={cn(
                'qs-shell-content-inner qs-shell-content-inner--fill',
                fallbackPaddingClass
              )}
              style={contentInnerStyle}
            >
              {guardedChildren}
            </div>
          </section>
        </main>
      </section>

      {shouldRenderWebBottomNav && handleNavigate && (
        <MobileBottomNav
          user={user}
          currentView={currentView}
          onNavigate={handleNavigate}
          onLogout={onLogout || (() => {})}
          shellRuntime={shellRuntime}
          nativePlatform={nativeBridge.platform}
          isNative={nativeBridge.isNative}
        />
      )}
    </section>
  );
}
